/**
 * TextAnswerQuestion Component
 * Free-text answer question type - user types their answer
 */

import React from 'react';
import { CheckCircle, XCircle, Info } from 'lucide-react';
import { QuestionLearner } from '@/types/assessment.types';

export interface TextAnswerQuestionProps {
  question: QuestionLearner;
  answer: string;
  onChange: (text: string) => void;
  disabled?: boolean;
  showCorrect?: boolean;
  acceptedAnswers?: string[]; // For results view
}

export const TextAnswerQuestion: React.FC<TextAnswerQuestionProps> = ({
  question,
  answer,
  onChange,
  disabled = false,
  showCorrect = false,
  acceptedAnswers = [],
}) => {
  const isCorrect = showCorrect && acceptedAnswers.length > 0;

  return (
    <div className="space-y-3">
      {/* Question Text */}
      <div className="flex items-start gap-2 mb-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{question.question_text}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-gray-100 rounded">{question.points} points</span>
          </div>
        </div>
      </div>

      {/* Text Input */}
      <div className="space-y-2">
        <textarea
          value={answer}
          onChange={e => !disabled && onChange(e.target.value)}
          disabled={disabled}
          placeholder="Type your answer here..."
          rows={4}
          className={`w-full p-3 border rounded-lg text-sm transition-colors ${
            showCorrect
              ? isCorrect
                ? 'border-green-300 bg-green-50'
                : 'border-red-300 bg-red-50'
              : 'border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20'
          } ${disabled ? 'cursor-not-allowed bg-gray-50' : ''}`}
        />

        {/* Character Count */}
        {!disabled && (
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{answer.length} characters</span>
            {answer.length === 0 && (
              <div className="flex items-center gap-1 text-amber-600">
                <Info className="w-3 h-3" />
                <span>Answer cannot be empty</span>
              </div>
            )}
          </div>
        )}

        {/* Results View - Show Correct/Incorrect */}
        {showCorrect && (
          <div
            className={`flex items-start gap-2 p-3 rounded-lg border ${
              isCorrect
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            {isCorrect ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900 mb-1">Correct Answer!</p>
                  <p className="text-xs text-green-700">Your answer matches the expected response.</p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900 mb-1">Incorrect Answer</p>
                  {acceptedAnswers.length > 0 && (
                    <div className="text-xs text-red-700">
                      <p className="font-medium mb-1">Accepted answers include:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {acceptedAnswers.slice(0, 3).map((accepted, idx) => (
                          <li key={idx}>{accepted}</li>
                        ))}
                        {acceptedAnswers.length > 3 && (
                          <li className="text-red-600">... and {acceptedAnswers.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
